import React, { Suspense, lazy, memo, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion } from 'framer-motion';
import './App.css';

// Components
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import SEO from './components/SEO';

// Lazy loaded components with preloading
const Features = lazy(() => 
  import('./components/Features').then(module => {
    // Preload related components
    import('./components/About');
    return module;
  })
);
const About = lazy(() => 
  import('./components/About').then(module => {
    // Preload related components
    import('./components/Solutions');
    return module;
  })
);
const Solutions = lazy(() => 
  import('./components/Solutions').then(module => {
    // Preload related components
    import('./components/Contact');
    return module;
  })
);
const Contact = lazy(() => import('./components/Contact'));
const NotFound = lazy(() => import('./components/NotFound'));

// Optimized loading spinner component
const LoadingSpinner = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
    <div className="text-center">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
      </div>
      <p className="text-slate-300 text-lg font-medium">Cognisafe yükleniyor...</p>
      <p className="text-slate-500 text-sm mt-2">AI destekli güvenlik platformu hazırlanıyor</p>
    </div>
  </div>
));

// Memoized page wrapper component
const PageWrapper = memo(({ children, initial = { opacity: 0 }, animate = { opacity: 1 }, transition = { duration: 0.5 } }) => (
  <motion.div initial={initial} animate={animate} transition={transition}>
    {children}
  </motion.div>
));

function App() {
  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen bg-background">
          <Header />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            <Route path="/" element={
              <PageWrapper>
                <SEO 
                  title="Cognisafe - AI Destekli İnsan Odaklı Siber Güvenlik Platformu | Ana Sayfa"
                  description="Cognisafe, yapay zeka destekli davranış analizi ile siber güvenlik risklerini %95 doğrulukla tespit eden yenilikçi platform. Proaktif tehdit önleme ve uyarlanabilir öğrenme motoru."
                  url="https://cognisafe.com.tr/"
                />
                <Hero />
                <Features />
                <About />
                <Solutions />
                <Contact />
              </PageWrapper>
            } />
            <Route path="/ozellikler" element={
              <PageWrapper>
                <SEO 
                  title="Özellikler - AI Destekli Davranış Analizi | Cognisafe"
                  description="Cognisafe'in AI destekli davranış analizi, proaktif tehdit önleme ve uyarlanabilir öğrenme motoru özellikleri. %95 doğruluk oranı ile siber güvenlik."
                  keywords="AI davranış analizi, tehdit önleme, siber güvenlik özellikleri, yapay zeka güvenlik, cognisafe özellikler"
                  url="https://cognisafe.com.tr/ozellikler"
                />
                <Features />
              </PageWrapper>
            } />
            <Route path="/hakkimizda" element={
              <PageWrapper>
                <SEO 
                  title="Hakkımızda - İnsan Odaklı Siber Güvenlik | Cognisafe"
                  description="Cognisafe ekibi ve vizyonu. İnsan odaklı siber güvenlik alanında Türkiye'nin öncü platformu. Yapay zeka ve davranış bilimini birleştiriyoruz."
                  keywords="cognisafe hakkında, siber güvenlik ekibi, insan odaklı güvenlik, cognisafe vizyon"
                  url="https://cognisafe.com.tr/hakkimizda"
                />
                <About />
              </PageWrapper>
            } />
            <Route path="/cozumler" element={
              <PageWrapper>
                <SEO 
                  title="Çözümler - Siber Güvenlik Platformu | Cognisafe"
                  description="Cognisafe siber güvenlik çözümleri. Kurumsal güvenlik, risk yönetimi ve davranış analizi çözümleri ile işletmenizi koruyun."
                  keywords="siber güvenlik çözümleri, kurumsal güvenlik, risk yönetimi, cognisafe çözümler"
                  url="https://cognisafe.com.tr/cozumler"
                />
                <Solutions />
              </PageWrapper>
            } />
            <Route path="/iletisim" element={
              <PageWrapper>
                <SEO 
                  title="İletişim - Cognisafe Siber Güvenlik Platformu"
                  description="Cognisafe ile iletişime geçin. Siber güvenlik çözümleri hakkında bilgi alın ve demo talep edin. İstanbul, Türkiye."
                  keywords="cognisafe iletişim, siber güvenlik demo, cognisafe telefon, cognisafe adres"
                  url="https://cognisafe.com.tr/iletisim"
                />
                <Contact />
              </PageWrapper>
            } />
            <Route path="*" element={
              <PageWrapper>
                <SEO 
                  title="404 - Sayfa Bulunamadı | Cognisafe"
                  description="Aradığınız sayfa bulunamadı. Cognisafe ana sayfasına dönün veya diğer sayfalarımızı keşfedin."
                  url="https://cognisafe.com.tr/404"
                />
                <NotFound />
              </PageWrapper>
            } />
          </Routes>
          </Suspense>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
